<script setup lang="ts">
import { ref } from 'vue'
import { useAuditStore } from '@/stores/audit'
import { DomainSchema, OptionalEmailSchema, firstIssue } from '@/validation'
import { maxLength, tidy } from '@/lib/inputFormat'

const store = useAuditStore()

const domain = ref('')
const email = ref('')

function tidyDomain() {
  domain.value = tidy('text', domain.value) as string
}
function tidyEmail() {
  email.value = tidy('email', email.value) as string
}

async function onSubmit() {
  const issue =
    firstIssue(DomainSchema.safeParse(domain.value)) ??
    firstIssue(OptionalEmailSchema.safeParse(email.value))
  if (issue !== undefined) {
    store.status = 'error'
    store.error = issue
    return
  }
  await store.submit(domain.value, email.value)
}
</script>

<template>
  <form id="audit-form" @submit.prevent="onSubmit">
    <div class="field">
      <label for="domain">Your website</label>
      <input
        id="domain"
        v-model="domain"
        type="text"
        name="domain"
        placeholder="example.com"
        autocomplete="url"
        inputmode="url"
        autocapitalize="off"
        spellcheck="false"
        enterkeyhint="go"
        :maxlength="maxLength.text"
        required
        :disabled="store.status === 'running'"
        @blur="tidyDomain"
      />
    </div>
    <div class="field">
      <label for="email">Your email</label>
      <input
        id="email"
        v-model="email"
        type="email"
        name="email"
        placeholder="you@example.com"
        autocomplete="email"
        autocapitalize="off"
        spellcheck="false"
        enterkeyhint="go"
        :maxlength="maxLength.text"
        :disabled="store.status === 'running'"
        @blur="tidyEmail"
      />
      <small class="field-hint"> Optional. Leave it blank and you still get the report. </small>
    </div>
    <button type="submit" class="btn btn-primary" :disabled="store.status === 'running'">
      {{ store.status === 'running' ? 'Checking…' : 'Check my website' }}
    </button>
  </form>

  <p v-if="store.status === 'error'" class="form-error" role="alert">
    {{ store.error }}
  </p>
</template>

<style scoped>
#audit-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
}

.field input {
  background: var(--bg);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.7rem 0.9rem;
  font-size: 1rem;
  font-family: inherit;
}
.field input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(162, 59, 29, 0.15);
}
.field input:disabled {
  opacity: 0.6;
}

.field-hint {
  color: var(--text-muted);
  font-size: 0.8rem;
}

.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: var(--radius);
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-align: center;
}
.btn-primary {
  background: var(--accent);
  color: var(--on-accent);
}
.btn-primary:hover {
  background: var(--accent-bright);
}
.btn[disabled] {
  opacity: 0.6;
  cursor: wait;
}

.form-error {
  margin-top: 1rem;
  background: var(--fail-tint);
  border: 1px solid var(--fail);
  border-radius: var(--radius);
  padding: 0.75rem 1rem;
  color: var(--text);
}
</style>
